import { useState, useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'

import config from './config'
import store from '../store'
import { setFlashMessage } from '../actions/flashActions'

const requestListeners = []

const addRequestListener = (onBefore, onAfter, onError) => {
  const listener = { onBefore, onAfter, onError }
  requestListeners.push(listener)
  return () => {
    const idx = requestListeners.indexOf(listener)
    if (idx > -1) requestListeners.splice(idx, 1)
  }
}

const reserveApi = async ({ method, url, data, params }) => {
  const token = store.getState().session.token
  const baseURL = config.reserveApiUrl

  const filteredParams = params
    ? Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
    : null
  const query = filteredParams && Object.keys(filteredParams).length ? '?' + new URLSearchParams(filteredParams) : ''
  const fullUrl = `${baseURL}${url}${query}`

  requestListeners.forEach(l => l.onBefore && l.onBefore(url))

  let response
  try {
    response = await fetch(fullUrl, {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Token token=${token}` } : {}),
      },
      body: data !== undefined ? JSON.stringify(data) : undefined,
    })
  } catch (networkError) {
    const error = new Error('Network error')
    error.response = { status: 0, data: null }
    requestListeners.forEach(l => l.onError && l.onError(error, url))
    throw error
  }

  if (!response.ok) {
    let errorData = null
    try { errorData = await response.json() } catch (_) {}
    const error = new Error(response.statusText)
    error.response = { status: response.status, data: errorData }
    requestListeners.forEach(l => l.onError && l.onError(error, url))
    throw error
  }

  let responseData = null
  try { responseData = await response.json() } catch (_) {}

  const result = { status: response.status, data: responseData }
  requestListeners.forEach(l => l.onAfter && l.onAfter(result, url))
  return result
}

export const useLoader = () => {
  const [counter, setCounter] = useState(0)
  const inc = useCallback(() => setCounter(c => c + 1), [])
  const dec = useCallback(() => setCounter(c => c - 1), [])

  const onBefore = useMemo(() => inc, [inc])
  const onAfter = useMemo(() => dec, [dec])
  const onError = useMemo(() => dec, [dec])

  useEffect(() => {
    return addRequestListener(onBefore, onAfter, onError)
  }, [onBefore, onAfter, onError])

  return counter > 0
}

export const useAdminLoader = () => {
  const [counter, setCounter] = useState(0)
  const isAdmin = useCallback(url => typeof url === 'string' && url.includes('/admin/'), [])

  useEffect(() => {
    return addRequestListener(
      url => { if (isAdmin(url)) setCounter(c => c + 1) },
      (_result, url) => { if (isAdmin(url)) setCounter(c => c - 1) },
      (_error, url) => { if (isAdmin(url)) setCounter(c => c - 1) },
    )
  }, [isAdmin])

  return counter > 0
}

export const useApiDown = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    return addRequestListener(null, null, error => {
      if (error.response?.status === 502) {
        dispatch(setFlashMessage('Unable to establish connection with Wandering Labs backend. Please attempt again in a moment.', 'error'))
      }
    })
  }, [dispatch])
}

export default reserveApi
export const getDefaultAdapter = () => null
